import React, { useState } from "react";
import axios from "axios";
import { Mail, Loader2, Send, CheckCircle2 } from "lucide-react";
import { useSite } from "../context/SiteContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { c } = useSite();
  const t = (c.contact) || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setErr(t.required || "Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      setErr(t.error || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ink min-h-screen">
      <section className="pt-36 sm:pt-44 pb-16 border-b border-[var(--c-border)]">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-3 mb-6 text-gold">
            <Mail className="w-4 h-4" />
            <span className="eyebrow" data-testid="contact-eyebrow">{t.eyebrow || "Contact"}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl font-light leading-[0.98] text-parchment max-w-3xl">
            {t.title || "Write to the family."}
          </h1>
          <p className="mt-7 text-lg sm:text-xl leading-relaxed text-parchment/65 font-light max-w-2xl">
            {t.intro || "Questions about the lineage, the cave, or your own Huchthausen name? Send a message and we will be in touch."}
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          {sent ? (
            <div data-testid="contact-success" className="border border-gold/40 bg-ink2 p-10 text-center">
              <CheckCircle2 className="w-12 h-12 text-gold mx-auto" />
              <h2 className="font-display text-3xl text-parchment mt-5">{t.successTitle || "Message sent"}</h2>
              <p className="mt-3 text-parchment/65 font-light">
                {t.successText || "Thank you — your message has reached the family. We will reply as soon as we can."}
              </p>
              <button
                onClick={() => setSent(false)}
                data-testid="contact-again-button"
                className="mt-8 font-label text-[0.7rem] uppercase tracking-[0.2em] text-ink bg-gold px-7 py-3.5 hover:opacity-90 transition-opacity"
              >
                {t.again || "Send another"}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-7">
                <label className="block">
                  <span className="font-label text-[0.6rem] uppercase tracking-[0.18em] text-gold">{t.name || "Name"} *</span>
                  <input
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={set("name")}
                    className="mt-2 w-full bg-transparent border-b border-parchment/25 focus:border-gold outline-none py-3 text-lg font-display text-parchment placeholder:text-parchment/30 transition-colors"
                    placeholder={t.namePh || "Your name"}
                  />
                </label>
                <label className="block">
                  <span className="font-label text-[0.6rem] uppercase tracking-[0.18em] text-gold">{t.email || "Email"} *</span>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    className="mt-2 w-full bg-transparent border-b border-parchment/25 focus:border-gold outline-none py-3 text-lg font-display text-parchment placeholder:text-parchment/30 transition-colors"
                    placeholder={t.emailPh || "you@example.com"}
                  />
                </label>
              </div>
              <label className="block">
                <span className="font-label text-[0.6rem] uppercase tracking-[0.18em] text-gold">{t.phone || "Phone"}</span>
                <input
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  className="mt-2 w-full bg-transparent border-b border-parchment/25 focus:border-gold outline-none py-3 text-lg font-display text-parchment placeholder:text-parchment/30 transition-colors"
                  placeholder={t.phonePh || "Optional"}
                />
              </label>
              <label className="block">
                <span className="font-label text-[0.6rem] uppercase tracking-[0.18em] text-gold">{t.message || "Message"} *</span>
                <textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={set("message")}
                  rows={6}
                  className="mt-2 w-full bg-transparent border-b border-parchment/25 focus:border-gold outline-none py-3 text-lg font-display text-parchment placeholder:text-parchment/30 transition-colors resize-none"
                  placeholder={t.messagePh || "Your message…"}
                />
              </label>
              {err && <p className="text-oxblood text-sm" data-testid="contact-error">{err}</p>}
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-3 font-label text-[0.72rem] uppercase tracking-[0.22em] text-ink bg-gold px-8 py-4 hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {t.send || "Send message"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
